import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Train, Heart, Brain, Moon } from 'lucide-react';
import CTA from '../../components/CTA';

const LisburnAnxietyStress: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Anxiety Treatment Lisburn | Commuter Stress Management</title>
        <meta
          name="description"
          content="Professional anxiety and stress hypnotherapy serving Lisburn from Belfast clinic (8 miles). Commuter stress, work anxiety, panic attacks. CNHC registered."
        />
        <meta name="keywords" content="anxiety treatment Lisburn, stress management Lisburn, commuter stress Lisburn, hypnotherapy Lisburn anxiety, panic attacks Lisburn" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/lisburn-anxiety-stress/" />
        <meta property="og:title" content="Anxiety Treatment Lisburn | Commuter Stress Management" />
        <meta property="og:description" content="Professional anxiety and stress hypnotherapy serving Lisburn. Specialized support for commuter stress, work anxiety, and professional burnout." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Anxiety Treatment Lisburn",
            "description": "CNHC registered hypnotherapy for anxiety and stress, serving Lisburn from Belfast clinic. Specializing in commuter stress, work anxiety, panic attacks, and professional burnout.",
            "url": "https://www.sharonclare-hypnotherapy.com/lisburn-anxiety-stress/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Anxiety and Stress Treatment",
            "areaServed": {
              "@type": "City",
              "name": "Lisburn",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Wellness Clinic (Ormeau Road)",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "addressRegion": "County Antrim",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How far is Belfast from Lisburn for anxiety treatment?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Our Belfast clinic is at Belfast Wellness Clinic, 228 Ormeau Road, in south Belfast, about 10 minutes from the city centre. From Lisburn it is the M1 into Belfast and then south along the Ormeau Road, typically around 20 minutes off-peak. Lisburn is also on the rail line into Belfast, with Botanic and City Hospital the closest stations to the clinic, so it is easily reached by Lisburn commuters who already travel toward Belfast for work. Online sessions are also available for maximum convenience."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with commuter stress?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely. Commuter stress is one of the most common issues I treat among Lisburn clients. The daily journey to Belfast, traffic pressures, time pressures, and the cumulative stress of commuting year after year significantly impacts mental health. Solution-focused hypnotherapy works by reducing your overall anxiety levels, teaching you relaxation techniques you can use during your commute, improving your sleep quality so you start each day less stressed, and building resilience to daily pressures."
                }
              },
              {
                "@type": "Question",
                "name": "Can I book sessions before or after work?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Clinic days and times vary at the Belfast clinic, and early evening appointments are available. Current availability and online booking are on the clinic's own page: https://belfastchiropracticclinic.janeapp.co.uk/#/staff_member/37. Many Lisburn clients who commute to Belfast for work find it convenient to fit hypnotherapy into their existing weekly routine. If you need weekend availability, online sessions can be scheduled at times that suit you."
                }
              },
              {
                "@type": "Question",
                "name": "Do you offer online anxiety treatment?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, secure online hypnotherapy sessions are available via video call for Lisburn residents. Online sessions provide the same professional, evidence-based treatment as in-person appointments for anxiety and stress with the added convenience of attending from your home. This option is ideal for those with busy schedules, childcare responsibilities, or anyone who simply prefers the comfort and convenience of online therapy."
                }
              },
              {
                "@type": "Question",
                "name": "How many sessions do I need for work-related anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Most clients see significant improvements within 4-6 sessions for work-related anxiety and commuter stress. The number varies depending on the severity of your anxiety, how long you've been experiencing it, and your individual circumstances. We'll discuss your specific needs during the initial consultation and create a treatment plan tailored to your situation."
                }
              },
              {
                "@type": "Question",
                "name": "Is hypnotherapy suitable for busy professionals?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely. Solution-focused hypnotherapy is evidence-based, professional, and time-efficient, fitting well with busy professional schedules. Many Lisburn professionals seek hypnotherapy for stress management, work-life balance, performance anxiety, or commuter stress, appreciating the practical tools and techniques. Sessions can often be scheduled around your existing Belfast commute, making anxiety treatment accessible without adding significant time commitment to your already busy schedule."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Anxiety & Stress Treatment for Lisburn
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Professional hypnotherapy for commuter stress and work anxiety
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-4 mb-3">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">8 miles</span> from Lisburn via M1 • <span className="font-semibold">15-20 min drive</span>
                </p>
              </div>
              <div className="flex items-center justify-center space-x-4">
                <Train className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">Train</span> to Botanic or City Hospital, the closest stations to the clinic
                </p>
              </div>
              <p className="text-gray-600 mt-3">Clinic days and times vary • Early evening appointments available • Online sessions available</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Anxiety Treatment for Lisburn Professionals
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                If you're living in Lisburn and struggling with commuter stress, work anxiety, panic attacks, or the constant feeling of being overwhelmed, you don't have to let anxiety control your life. I serve the Lisburn community from my professional Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, in south Belfast - the M1 into Belfast, then south along the Ormeau Road. The short drive makes accessing specialist anxiety treatment convenient for Lisburn residents. For commuters who already travel toward Belfast for work, our clinic location is easily accessible - many clients fit their sessions into their existing journeys.
              </p>
              <p>
                As Northern Ireland's fifth-largest city and a major commuter hub for Belfast, Lisburn faces unique challenges. The pressures of daily commuting on the M1, work-related stress, maintaining work-life balance, and the fast pace of modern professional life take their toll on mental and physical health. Many Lisburn residents juggle demanding careers, family responsibilities, and financial pressures, leaving little time for self-care until stress and anxiety become unmanageable. Sound familiar?
              </p>
              <p>
                Anxiety isn't just feeling stressed or worried - it's a genuine neurological condition where your brain's alarm system stays activated even when there's no real danger. Whether you're experiencing commuter anxiety that makes the M1 drive feel unbearable, work-related stress that follows you home every evening, panic attacks that come without warning, social anxiety affecting your confidence at work, generalized anxiety making every decision feel overwhelming, or sleep problems from racing thoughts, hypnotherapy addresses the root neurological cause rather than just managing symptoms.
              </p>
              <p>
                Solution-focused hypnotherapy offers an evidence-based approach to managing these challenges. The approach combines the latest neuroscience research with proven therapeutic techniques. Rather than dwelling on problems or analyzing the past, we focus on solutions, building resilience, and practical strategies for change. This forward-focused approach resonates with many Lisburn professionals who want to move beyond anxiety and create better coping mechanisms for handling life's inevitable pressures.
              </p>
              <p>
                For Lisburn residents, the convenience factor is significant. With the M1 running straight into Belfast and the Ormeau Road just south of the city centre (around 20 minutes off-peak), accessing professional anxiety treatment has never been easier. Lisburn is also on the Belfast rail line, and Botanic and City Hospital stations are the closest to the clinic. Alternatively, online sessions provide the same professional treatment from the comfort of your home in Lisburn.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Anxiety Affects Lisburn Professionals */}
      <section className="py-16 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">How Anxiety Affects Lisburn Professionals</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Commuter stress and work-related anxiety create unique challenges for Lisburn's professional population. The daily commute to Belfast, workplace pressures, and trying to maintain work-life balance create chronic stress that affects every area of your life. You might recognize these experiences:
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <ul className="space-y-3">
                {[
                  'M1 traffic stress adding to daily anxiety levels',
                  'Difficulty switching off from work mode when you get home',
                  'Racing thoughts preventing restful sleep',
                  'Physical symptoms like tight chest or headaches',
                  'Irritability with family despite wanting to be present',
                  'Panic attacks during commute or at work',
                  'Feeling overwhelmed by responsibilities',
                  'Constant worry about work performance or job security'
                ].map((item) => (
                  <li key={item} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-3">The Professional Toll</h3>
              <p className="text-gray-700 mb-4">
                When anxiety dominates your life, it affects everything. Work performance suffers when anxiety prevents clear thinking and confident decision-making. Commuter stress compounds daily, making the journey to and from Belfast increasingly unbearable. Relationships strain when you come home too exhausted and stressed to be present with family. Health deteriorates from poor sleep, tension, and stress-related conditions. The very lifestyle you're working hard to build in Lisburn feels impossible to enjoy because anxiety colors everything.
              </p>
              <p className="text-gray-700">
                Many Lisburn professionals push through until they hit a breaking point - a panic attack on the M1, complete burnout at work, or relationships reaching crisis point. But it doesn't have to reach that stage. Hypnotherapy can help you regain control before anxiety takes everything.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps with Commuter Anxiety */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">How Hypnotherapy Helps with Anxiety and Commuter Stress</h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            Solution-focused hypnotherapy doesn't just teach you to "cope better" with impossible demands. It works at a neurological level to genuinely reduce your anxiety, build resilience, and change how your brain responds to stress. The approach combines evidence-based techniques with deep relaxation to create lasting change.
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {[
              {
                icon: <Heart className="w-6 h-6 text-accent-600" />,
                title: 'Calm the Stress Response',
                text: 'Reduce the constant fight-or-flight activation that makes commuting and work feel unbearable. Lower physical symptoms like rapid heartbeat and muscle tension.'
              },
              {
                icon: <Brain className="w-6 h-6 text-accent-600" />,
                title: 'Build Resilience',
                text: 'Develop healthier neural pathways for handling pressure. Learn to respond to stress with calm problem-solving rather than anxious overwhelm.'
              },
              {
                icon: <Moon className="w-6 h-6 text-accent-600" />,
                title: 'Restore Sleep Quality',
                text: 'Quiet the work thoughts and commuter stress that keep you awake. Wake refreshed and better able to handle daily pressures without anxiety.'
              },
            ].map(({ icon, title, text }) => (
              <div key={title} className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-6">
                <div className="flex items-center space-x-3 mb-2">
                  {icon}
                  <h3 className="font-heading font-semibold text-gray-900">{title}</h3>
                </div>
                <p className="text-gray-700 text-sm">{text}</p>
              </div>
            ))}
          </div>
          <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
            <h3 className="font-heading font-semibold text-gray-900 mb-3">Results Lisburn Clients Experience</h3>
            <p className="text-gray-700 mb-4">
              Most Lisburn professionals notice significant improvements within 4-6 sessions. You'll experience feeling calmer during your Belfast commute (the M1 doesn't trigger the same anxiety), better ability to "switch off" when you get home to family, improved sleep despite work pressures, reduced physical symptoms of stress and anxiety, greater confidence at work and in social situations, and better emotional regulation when problems arise. You'll handle the same daily stresses with much greater calm and perspective.
            </p>
            <p className="text-gray-700">
              The convenient Belfast location means you can easily fit sessions around your work schedule. Many Lisburn clients schedule appointments on their regular commute days, either before work, during lunch, or on their way home, making anxiety treatment accessible without adding significant time commitment to your already busy life.
            </p>
          </div>
        </div>
      </section>

      {/* Types of Anxiety We Treat */}
      <section className="py-16 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">Types of Anxiety We Treat for Lisburn Clients</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-3">Anxiety Conditions</h3>
              <ul className="space-y-3">
                {[
                  'Commuter stress and travel anxiety',
                  'Work-related stress and burnout',
                  'Generalized Anxiety Disorder (GAD)',
                  'Panic attacks and panic disorder',
                  'Social anxiety in professional settings',
                  'Performance anxiety (meetings, presentations)',
                  'Health anxiety and hypochondria',
                  'Financial anxiety and money worries'
                ].map((item) => (
                  <li key={item} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h3 className="font-heading font-semibold text-gray-900 mb-3">Related Issues</h3>
              <ul className="space-y-3 mb-4">
                {[
                  'Sleep problems from work stress',
                  'Difficulty maintaining work-life balance',
                  'Relationship problems from stress and irritability',
                  'Physical symptoms (headaches, digestive issues)',
                  'Difficulty concentrating or making decisions',
                  'Feeling constantly overwhelmed'
                ].map((item) => (
                  <li key={item} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-4">
                <p className="text-gray-700 text-sm">
                  <span className="font-semibold">Specialized support also available for </span>
                  <a href="/medical-procedure-anxiety" className="text-accent-600 hover:text-accent-700 underline">medical anxiety</a> and{' '}
                  <a href="/burnout-recovery" className="text-accent-600 hover:text-accent-700 underline">burnout recovery</a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting to Belfast from Lisburn */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Getting to Belfast from Lisburn
            </h2>
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
                <div className="flex items-start space-x-4 mb-4">
                  <Car className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 text-lg">By Car</h3>
                    <p className="text-gray-700 mb-2">
                      <span className="font-semibold">Route:</span> M1 north from Lisburn into Belfast, then south along the Ormeau Road (A24)
                    </p>
                    <p className="text-gray-700 mb-2">
                      <span className="font-semibold">Destination:</span> Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ
                    </p>
                    <p className="text-gray-700 mb-2">
                      <span className="font-semibold">Time:</span> Around 20 minutes off-peak (traffic dependent)
                    </p>
                    <p className="text-gray-700">
                      <span className="font-semibold">Location:</span> South Belfast, roughly 10 minutes from the city centre
                    </p>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-4 mt-4">
                  <p className="text-gray-700 text-sm">
                    Many Lisburn commuters already make this journey daily for work, making anxiety treatment sessions easy to fit into existing travel patterns.
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
                <div className="flex items-start space-x-4 mb-4">
                  <Train className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 text-lg">By Train</h3>
                    <p className="text-gray-700 mb-2">
                      <span className="font-semibold">Route:</span> Lisburn to Botanic or City Hospital on the Belfast line
                    </p>
                    <p className="text-gray-700 mb-2">
                      <span className="font-semibold">Frequency:</span> Regular services throughout the day
                    </p>
                    <p className="text-gray-700 mb-2">
                      <span className="font-semibold">Journey Time:</span> Around 20 minutes into south Belfast
                    </p>
                    <p className="text-gray-700">
                      <span className="font-semibold">From Station:</span> Botanic and City Hospital are the closest stations to the clinic, with a short taxi ride or walk to the Ormeau Road
                    </p>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-4 mt-4">
                  <p className="text-gray-700 text-sm">
                    Train option is ideal if you prefer not to drive or want to relax during your journey to anxiety treatment.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
              <h3 className="font-semibold text-gray-900 mb-4 text-lg">Ideal for Commuters</h3>
              <p className="text-gray-700 mb-4">
                If you already commute to Belfast for work, attending hypnotherapy sessions requires no extra travel - many clients schedule appointments on their usual commute days, either before work, during lunch, or on their way home. The convenience factor makes regular anxiety treatment much more sustainable for busy Lisburn professionals.
              </p>
              <h3 className="font-semibold text-gray-900 mb-4 text-lg">Online Sessions Alternative</h3>
              <p className="text-gray-700">
                Prefer to stay in Lisburn? Secure online hypnotherapy sessions provide the same professional treatment from your home. This option is perfect for non-commuting days, those with childcare responsibilities, or anyone who values the convenience of online therapy. The therapeutic effectiveness is equivalent to in-person sessions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Lisburn Clients Say About Anxiety Treatment
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "The drive from Lisburn was so easy on the M1. Sharon helped me with the commuter stress that was dominating my life. I scheduled my sessions on Thursday mornings before work, so it didn't interfere with my routine at all. After 5 sessions, the M1 doesn't trigger anxiety anymore and I'm actually sleeping properly for the first time in years."
                </p>
                <p className="font-semibold text-gray-900">A.K., Lisburn - Commuter Stress and Anxiety</p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Convenient for my commute. I booked my session on the way home from work in Belfast - literally added 10 minutes to my journey. Sharon's approach to work stress and anxiety was exactly what I needed. No more panic attacks, sleeping better, and I actually enjoy my commute now instead of dreading it. My whole family has noticed the difference."
                </p>
                <p className="font-semibold text-gray-900">J.M., Lisburn - Work Anxiety and Panic Attacks</p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I was heading for complete burnout - work stress, family pressures, constant anxiety about everything. The online sessions from Sharon were perfect because I could do them from home in Hillsborough without adding more travel stress. My whole outlook has changed. I feel in control again instead of constantly overwhelmed by anxiety."
                </p>
                <p className="font-semibold text-gray-900">L.P., Hillsborough - Anxiety and Burnout</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Lisburn Anxiety Treatment
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How far is Belfast from Lisburn for anxiety treatment?
                </h3>
                <p className="text-gray-700">
                  Our Belfast clinic is at Belfast Wellness Clinic, 228 Ormeau Road, in south Belfast - the M1 into Belfast from Lisburn, then south along the Ormeau Road, typically around 20 minutes off-peak. Lisburn is also on the rail line into Belfast, with Botanic and City Hospital the closest stations to the clinic. For many Lisburn commuters who already travel toward Belfast for work, our clinic location is easily accessible, making anxiety treatment highly convenient.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with commuter stress?
                </h3>
                <p className="text-gray-700">
                  Absolutely. Commuter stress is one of the most common issues I treat among Lisburn clients. The daily journey to Belfast, M1 traffic pressures, time pressures, and the cumulative stress of commuting year after year significantly impacts mental health and creates chronic anxiety. Solution-focused hypnotherapy works by reducing your overall anxiety levels, teaching you relaxation techniques you can use during your commute, improving your sleep quality (so you start each day less stressed), and building resilience to daily pressures. Most clients notice they feel calmer during their commute within just a few sessions.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can I book sessions before or after work?
                </h3>
                <p className="text-gray-700">
                  Clinic days and times vary at the Belfast clinic, and early evening appointments are available. Current availability and online booking are on the clinic's own page at <a href="https://belfastchiropracticclinic.janeapp.co.uk/#/staff_member/37" target="_blank" rel="noopener noreferrer" className="text-accent-600 hover:text-accent-700 underline">belfastchiropracticclinic.janeapp.co.uk</a>. The mix of daytime and early evening slots works well for Lisburn clients who commute or work flexibly, and many professionals schedule sessions on their regular commute days. If you need weekend availability, online sessions can be scheduled at times that suit you.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Do you offer online anxiety treatment?
                </h3>
                <p className="text-gray-700">
                  Yes, secure online hypnotherapy sessions are available via video call for Lisburn residents. Online sessions provide the same professional, evidence-based treatment for anxiety and stress as in-person appointments with the added convenience of attending from your home. This option is ideal for those with busy schedules, childcare responsibilities, or anyone who simply prefers the comfort and convenience of online therapy. Many clients start with in-person sessions to build rapport and then switch to online for ongoing treatment, enjoying the flexibility to choose what works best for each appointment.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How many sessions do I need for work-related anxiety?
                </h3>
                <p className="text-gray-700">
                  Most clients see significant improvements within 4-6 sessions for work-related anxiety and commuter stress. The number varies depending on the severity of your anxiety, how long you've been experiencing it, and your individual circumstances. Generalized work anxiety often responds well within 4-6 sessions, while more complex issues like panic disorder or burnout might benefit from 6-8 sessions. We'll discuss your specific needs during the initial consultation and create a treatment plan tailored to your situation. I also offer a discounted 4-session package which provides better value for clients committing to anxiety treatment.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is hypnotherapy suitable for busy professionals?
                </h3>
                <p className="text-gray-700">
                  Absolutely. Solution-focused hypnotherapy is evidence-based, professional, and time-efficient, fitting well with busy professional schedules. Many Lisburn professionals seek hypnotherapy for stress management, work-life balance, performance anxiety, or commuter stress, appreciating the practical tools and techniques rather than lengthy analysis. Sessions can often be scheduled around your existing Belfast commute - many clients book appointments on their regular work days, either before work, during lunch, or on their way home. This makes anxiety treatment accessible without adding significant time commitment to your already busy schedule.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA
        title="Take Control of Your Anxiety Today"
        subtitle="Stop letting commuter stress and work anxiety control your life. Book a consultation and discover how hypnotherapy can help you regain calm and balance."
        primaryButtonText="Request a 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call 07932 139245"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default LisburnAnxietyStress;
